<template>
  <v-card width="100%" class="elevation-4 mb-4">
    <!-- CHART -->
    <div :style="ui.chartMaxHeight">
      <canvas :id="chartName"></canvas>
    </div>

    <!-- TITLE OF Chart -->
    <v-card-title primary-title>
      <div>
        <h3 class="text-h5 mb0">{{ name }}</h3>
      </div>
    </v-card-title>
  </v-card>
</template>

<script>
import 'chartjs-plugin-colorschemes'
import Chart from 'chart.js'
import 'chartjs-plugin-datalabels'
import { CHART_COLORS } from '@/components/charts'

const { v4: _uuid } = require('uuid')

// Label formatter function
const labelFormatter = (value, ctx) => {
  const otherDatasetIndex = ctx.datasetIndex % 2 === 0 ? ctx.datasetIndex + 1 : Math.max(0, ctx.datasetIndex - 1)
  const total = ctx.chart.data.datasets[otherDatasetIndex].data.reduce((accum, val) => {
    return accum + val
  }, 0) + value

  if (value > 0) {
    return `${value} - ${(value / total * 100).toFixed(0)}%`
  }
}

const shouldDisplay = (context) => {
  return context.dataset.data[context.dataIndex] !== 0 // or >= 1 or ...
}

const zeroArray = (len, position, val) => {
  const za = Array(len).fill(0)
  za[position] = val
  return za
}

export default {
  name: 'vs-stacked-bar-chart',
  props: {
    name: {
      type: String,
      required: true
    },
    vsData: {
      type: Array,
      required: true
    }
  },
  data: function () {
    return {
      ui: {
        chartMaxHeight: 'height: 300px'
      },
      chartName: _uuid(),
      chart: null
    }
  },
  computed: {
    xLabels () {
      // e.g ['Conquest Vs Repeat', 'New vs Used', 'Finance vs Lease', 'Individual vs Business']
      return this.vsData.map(vsPair => {
        return `${vsPair[0].label} vs ${vsPair[1].label}`
      })
    },
    datasets () {
      return this.vsData.flatMap((vsPair, outerIdx) => {
        return vsPair.map(vs => {
          return this.datasetItem(vs.label, vs.data, outerIdx)
        })
      })
    },
    chartData () {
      return {
        type: 'bar',
        data: {
          labels: this.xLabels,
          datasets: this.datasets
        },
        options: {
          maintainAspectRatio: false,
          spanGaps: false,
          responsive: true,
          legend: {
            display: true,
            position: 'bottom',
            labels: {
              // fontColor: '#000',
              boxWidth: 14,
              fontFamily: 'proximanova'
            }
          },
          tooltips: {
            mode: 'label',
            callbacks: {
              label: function (tooltipItem, data) {
                // tooltipeItem = A vs B
                // data = labels: ["A vs B", "C vs D"], datasets [A,B,C,D]
                const dsIdx = data.labels.indexOf(tooltipItem.label) * 2
                const categoryDs = data.datasets.slice(dsIdx, dsIdx + 2) // +2 b/c js range is exclusive

                const type = data.datasets[tooltipItem.datasetIndex].label
                const value = data.datasets[tooltipItem.datasetIndex].data[tooltipItem.index]

                const hasLabelEntry = type === categoryDs[0].label || type === categoryDs[1].label

                if (data.datasets[tooltipItem.datasetIndex]) {
                  if (hasLabelEntry) {
                    return (
                      type + ' : ' + value.toFixed(0).replace(/(\d)(?=(\d{3})+\.)/g, '1,')
                    )
                  }
                }
              }
            }
          },
          plugins: {
            // Change options for ALL labels of THIS CHART
            datalabels: {
              color: '#000000',
              align: 'center'
            },
            colorschemes: {
              scheme: CHART_COLORS
            }
          },
          scales: {
            xAxes: [{
              stacked: true,
              gridLines: {
                display: true
              },
              ticks: {
                // fontColor: '#000'
              }
            },
            {
              type: 'category',
              offset: true,
              position: 'top',
              ticks: {
                // fontColor: '#000', // total at the top #
                callback: function (value, index, values) {
                  // example - value=A, index=0, values=[A,B,C]
                  const datasetIdx = values.indexOf(value) * 2 // a vs b == label:a_vs_b, dataset: [val * 2]
                  return this.datasets[datasetIdx].data[index] + this.datasets[datasetIdx + 1].data[index]
                }.bind(this)
              }
            }
            ],
            yAxes: [{
              stacked: true,
              display: true,
              ticks: {
                // fontColor: '#fff' // turn on/off Y Axis numbers w/ color
              }
            }]
          }
        }
      }
    }
  },
  watch: {
    vsData: {
      immediate: false,
      handler (newVal, oldVal) {
        this.refreshChart()
      }
    }
  },
  methods: {
    createChart (chartId, chartData) {
      if (this.chart) {
        this.chart.destroy() // else strange behavior
      }
      const ctx = document.getElementById(chartId)
      const chart = new Chart(ctx, {
        type: chartData.type,
        data: chartData.data,
        options: chartData.options
      })
      this.chart = chart
      return chart
    },
    refreshChart () {
      this.createChart(this.chartName, this.chartData)
    },
    datasetItem (label, value, datasetIndex) {
      return {
        stack: 'stack1',
        label: label,
        // backgroundColor: '#1d3f74',
        data: zeroArray(this.vsData.length, datasetIndex, value),
        datalabels: {
          // color: 'white',
          formatter: labelFormatter,
          display: shouldDisplay
        }
      }
    }
  }
}
</script>

<style>
</style>
